import React, { useCallback } from 'react';
import cn from 'classnames';

import IMG_SLICK from 'assets/images/logos/slick-logo-text.png';
import validatePassword from 'bl/validation/validatePassword.bl';
import validateVerificationCode from 'bl/validation/validateVerificationCode.bl';
import Button from 'components/form/Button.component';
import CodeInput from 'components/form/CodeInput.component';
import FormField from 'components/form/FormField.component';
import PasswordInput from 'components/form/PasswordInput.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Space from 'components/generic/Space.component';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';

import CN from './ChangePasswordPanel.module.scss';

const CTAG = 'ChangePasswordPanel';

const ChangePasswordPanel = ({ tag: tagFromProps, onNext }) => {
  const tag = fromAnyToTrimmed(tagFromProps);
  const { t } = useTranslation();

  const [code, setCode] = useStateWhileMounted(S.empty);
  const [pass, setPass] = useStateWhileMounted(S.empty);

  const isCodeValid = validateVerificationCode({ value: code });
  const isPassValid = validatePassword({ value: pass });
  const disabled = !isCodeValid || !isPassValid;

  const handleCode = useCallback(
    ({ string, is }) => {
      setCode(string);

      const isFullyEntered = is.valid;
      if (!isFullyEntered) return { error: null };

      return {
        error: validateVerificationCode({ value: string }) ? null : t('forgotPassword.changePasswordPanel.codeError'),
      };
    },
    [setCode, t],
  );

  const handlePass = useCallback(
    ({ dirty, string }) => {
      setPass(string);

      if (!dirty) return { error: null };
      return { error: validatePassword({ value: string }) ? null : t('common.errors.validation.passwordErrorMsg') };
    },
    [setPass, t],
  );

  const handleNext = useCallback(
    //
    () => {
      if (disabled) return;
      call(`${CTAG}.onNext`, onNext, { tag, code, pass });
    },
    [code, disabled, onNext, pass, tag],
  );

  const inputClasses = cn({
    [CN.inputContainer]: true,
    [CN.hasError]: disabled && !isPassValid && pass.trim() !== '',
  });

  return (
    <div data-bem="ChangePasswordPanel" className={CN.component}>
      <img src={IMG_SLICK} alt="Slick logo" className={CN.logo} />
      <div className={CN.title}>
        {t('forgotPassword.changePasswordPanel.title.part1')}
        {S.space}
        <span className={CN.bold}>{t('forgotPassword.changePasswordPanel.title.part2')}</span>
        {S.space}
        {t('forgotPassword.changePasswordPanel.title.part3')} {S.userLogin}
      </div>
      <div className={CN.subTitle}>{t('forgotPassword.changePasswordPanel.description')}</div>
      <FormField label={t('forgotPassword.changePasswordPanel.code')} input={CodeInput} onChange={handleCode} onKeyDown={handleCode} autofocus />
      {isCodeValid && (
        <div className={inputClasses}>
          <FormField
            input={PasswordInput}
            name="password"
            label={t('forgotPassword.changePasswordPanel.passwordLabel')}
            placeholder={t('forgotPassword.changePasswordPanel.passwordPlaceholder')}
            onChange={handlePass}
            renderError="always"
          />
        </div>
      )}
      <div className={CN.alertOuter}>
        <AlertPanel tag="ChangePasswordPanel__alert" variant="alert">
          <div>
            <Space bold>{t('forgotPassword.changePasswordPanel.alertMessage.part1')}</Space>
            {t('forgotPassword.changePasswordPanel.alertMessage.part2')}
          </div>
        </AlertPanel>
      </div>
      <div className={CN.buttonContainer}>
        <Button variant="primaryForce" width="max" height="max" onClick={handleNext} disabled={disabled}>
          <span className={CN.buttonFont}>{t('forgotPassword.changePasswordPanel.resetButton')}</span>
        </Button>
      </div>
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default ChangePasswordPanel;
